import { useRef } from 'react';
import i18n from 'helpers/i18n';
import messages from './messages';
import { FocusEvent, KeyboardEvent } from 'react';
import { TextArea, Label } from '@teachstonellc/component-library';

interface PlaceholderRange {
  start: number;
  end: number;
}

/** Finds every `[bracketed]` span in `text`, in the order they appear. */
export function findPlaceholders(text: string): PlaceholderRange[] {
  const placeholders: PlaceholderRange[] = [];
  const regex = /\[[^\]]+\]/g;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    placeholders.push({
      start: match.index,
      end: match.index + match[0].length
    });
  }

  return placeholders;
}

interface Snippet {
  label: string;
  template: string;
}

function getSnippets(): Snippet[] {
  return [
    {
      label: i18n.ft(messages.buttonEffectivePractices),
      template: i18n.ft(messages.snippetEffectivePractices)
    },
    {
      label: i18n.ft(messages.buttonGrowthAreas),
      template: i18n.ft(messages.snippetGrowthAreas)
    },
    {
      label: i18n.ft(messages.buttonNextSteps),
      template: i18n.ft(messages.snippetNextSteps)
    }
  ];
}

interface FeedbackTextAreaProps {
  /** Accessible name -- the question text -- kept distinct per field since
   * several can be expanded at once; the visible label is the generic
   * "Educator Feedback" caption instead (see `messages.textAreaLabel`). */
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: (event: FocusEvent<HTMLTextAreaElement>) => void;
  dataQuestionId?: number;

  /** Inline validation message shown underneath the textarea. */
  error?: string | null;
}

/**
 * The Observation Feedback textarea, plus buttons that insert an editable
 * sentence starter as a new line. After inserting, the snippet's first
 * `[placeholder]` -- brackets included -- is selected so the observer can
 * start typing over it immediately without deleting them by hand; `Tab`
 * jumps to the next `[placeholder]` still ahead of the cursor (a snippet can
 * have more than one), then falls back to normal focus movement once none
 * remain.
 */
export function FeedbackTextArea({
  label,
  value,
  onChange,
  onBlur,
  dataQuestionId,
  error
}: FeedbackTextAreaProps) {
  const ref = useRef<HTMLTextAreaElement>(null);

  function selectPlaceholder(placeholder: PlaceholderRange) {
    // Includes the brackets, so typing over the selection clears them too.
    ref.current?.setSelectionRange(placeholder.start, placeholder.end);
  }

  function insertSnippet(template: string) {
    const needsNewline = value.length > 0 && !value.endsWith('\n');
    const prefix = needsNewline ? `${value}\n` : value;
    const insertionStart = prefix.length;

    onChange(prefix + template);

    const [first] = findPlaceholders(template);

    // The DOM value hasn't updated yet when this handler runs synchronously.
    requestAnimationFrame(() => {
      ref.current?.focus();
      if (first) {
        selectPlaceholder({
          start: first.start + insertionStart,
          end: first.end + insertionStart
        });
      }
    });
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Tab' || event.shiftKey) return;

    const textarea = event.currentTarget;

    // Searched fresh against the current value rather than positions
    // recorded at insert time: those go stale the moment the observer types
    // over an earlier placeholder, since that shifts everything after it.
    const next = findPlaceholders(value).find(
      placeholder => placeholder.start >= textarea.selectionEnd
    );
    if (!next) return;

    event.preventDefault();
    selectPlaceholder(next);
  }

  return (
    // `h-full` picks up the height the grid row stretches this column to
    // (matching the Ranges table/Evidence Notes column next to it). The
    // `grid` wrapper around `TextArea` exists because its own root element
    // isn't ours to give `flex-1`: a single-item CSS grid stretches its item
    // to fill the grid's height by default (unlike a flex child, which
    // wouldn't grow without `flex-1` on that same element), so that root
    // element ends up full height without us touching it; the `flex-1` we
    // *can* set, on the `<textarea>` itself via `className`, then grows it
    // to fill that. `min-h-28` is the fallback height where there's no
    // taller sibling to match, e.g. stacked on mobile.
    <div className="flex h-full flex-col">
      <div className="grid min-h-0 flex-1">
        <TextArea
          ref={ref}
          // `grow` (not `flex-1`) leaves `flex-basis` at its default `auto`,
          // which resolves to the manually-resized height once the observer
          // drags the handle -- `flex-1`'s `flex-basis: 0%` would make grow
          // recompute the height every render and silently undo a manual
          // resize. `shrink-0` keeps that size from being clawed back too.
          className="min-h-28 grow shrink-0 resize-y"
          label={i18n.ft(messages.textAreaLabel)}
          showLabel
          placeholder={i18n.ft(messages.feedbackTextAreaPlaceholder)}
          aria-label={label}
          data-question-id={dataQuestionId}
          value={value}
          error={error || undefined}
          onChange={event => onChange(event.currentTarget.value)}
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
        />
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        {getSnippets().map(snippet => (
          <button
            key={snippet.label}
            type="button"
            className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-border-focus"
            onClick={() => insertSnippet(snippet.template)}
          >
            <Label>{snippet.label}</Label>
          </button>
        ))}
      </div>
    </div>
  );
}
